import { Component, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { ApiService } from 'src/app/shared/services/api.service';
import { ToastrMsgService } from 'src/app/shared/services/shared-services/toastr.service';
import { BillDetailsDialogComponent } from './bill-details-dialog/bill-details-dialog.component';
import { EditBillAmountDialogComponent } from './edit-bill-amount-dialog/edit-bill-amount-dialog.component';

export interface ProjectBill {
  id: number;
  project_id: number;
  project_common_id: number;
  project_name: string;
  project_type: string;
  domain: string;
  sub_domain?: string;
  area: number;
  road_length: number;
  unit_rate: number;
  quantity: number;
  bill_amount: number;
  rate_type: string;
  base_rate: number;
  status: string;
  billing_date: string;
  payment_date: string;
  invoice_number: string;
  created_at: string;
  project_name_full?: string;
  project_code?: string;
  org_name?: string;
}

@Component({
  selector: 'app-project-bills',
  templateUrl: './project-bills.component.html',
  styleUrls: ['./project-bills.component.css']
})
export class ProjectBillsComponent implements OnInit, AfterViewInit {
  displayedColumns: string[] = [
    'project_name',
    'domain',
    'project_type',
    'quantity',
    'unit_rate',
    'bill_amount',
    'status',
    'invoice_number',
    'billing_date',
    'payment_date',
    'actions'
  ];

  dataSource = new MatTableDataSource<ProjectBill>([]);
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  loading = false;
  searchKeyword = '';
  selectedStatus: string = '';
  selectedProjectType: string = '';
  selectedProject: string = '';

  // Pagination
  totalItems = 0;
  pageSize = 10;
  pageIndex = 0;
  pageSizeOptions = [5, 10, 20, 50];

  // Summary data
  summary: any = null;
  summaryLoading = false;

  statusOptions = [
    { value: '', label: 'All Status' },
    { value: 'pending', label: 'Pending' },
    { value: 'billed', label: 'Billed' },
    { value: 'paid', label: 'Paid' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  projectTypeOptions = [
    { value: '', label: 'All Types' },
    { value: 'CONSTRUCTION', label: 'Construction' },
    { value: 'ROAD', label: 'Road' },
    { value: 'UTILITIES', label: 'Utilities' },
    { value: 'CONSULTANCY', label: 'Consultancy' }
  ];

  isSuperAdmin = false;

  constructor(
    private apiService: ApiService,
    private toastrService: ToastrMsgService,
    private dialog: MatDialog
  ) {
    this.checkSuperAdmin();
  }

  checkSuperAdmin() {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      // Check if user is superadmin (role_id === 1 or role.type === 1)
      this.isSuperAdmin = user?.role_id === 1 || user?.role?.type === 1 || user?.user?.role_id === 1;
    } catch (error) {
      console.error('Error checking superadmin status:', error);
      this.isSuperAdmin = false;
    }
  }

  ngOnInit() {
    this.loadProjectBills();
    this.loadSummary();
  }

  ngAfterViewInit() {
    this.dataSource.sort = this.sort;
  }

  loadProjectBills() {
    this.loading = true;
    const params: any = {
      page: this.pageIndex + 1,
      limit: this.pageSize
    };

    if (this.searchKeyword) {
      params.search = this.searchKeyword;
    }

    if (this.selectedStatus) {
      params.status = this.selectedStatus;
    }

    if (this.selectedProjectType) {
      params.project_type = this.selectedProjectType;
    }

    if (this.selectedProject) {
      params.project_id = this.selectedProject;
    }

    this.apiService.getProjectBills(params).subscribe({
      next: (res: any) => {
        if (res.success) {
          // Convert string numbers to actual numbers for proper formatting
          const bills = res.data.map((bill: any) => ({
            ...bill,
            area: parseFloat(bill.area) || 0,
            road_length: parseFloat(bill.road_length) || 0,
            unit_rate: parseFloat(bill.unit_rate) || 0,
            quantity: parseFloat(bill.quantity) || 0,
            bill_amount: parseFloat(bill.bill_amount) || 0,
            base_rate: parseFloat(bill.base_rate) || 0
          }));
          this.dataSource.data = bills;
          this.totalItems = res.pagination?.total || 0;
        } else {
          this.toastrService.error('Failed to load project bills');
        }
        this.loading = false;
      },
      error: (err: any) => {
        console.error('Error loading project bills:', err);
        this.toastrService.error(err.error?.error || 'Failed to load project bills');
        this.loading = false;
      }
    });
  }

  loadSummary() {
    this.summaryLoading = true;
    this.apiService.getProjectBillsSummary().subscribe({
      next: (res: any) => {
        if (res.success) {
          this.summary = res.data;
        }
        this.summaryLoading = false;
      },
      error: (err: any) => {
        console.error('Error loading summary:', err);
        this.summaryLoading = false;
      }
    });
  }

  applyFilter() {
    this.pageIndex = 0;
    this.loadProjectBills();
  }

  onPageChange(event: PageEvent) {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadProjectBills();
  }

  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  }

  formatQuantity(quantity: number, rateType: string): string {
    if (rateType === 'per_project') {
      return '1 Project';
    }
    // Both per_sq_mt and per_psm use square meters
    return `${quantity.toFixed(2)} sqm`;
  }

  getStatusClass(status: string): string {
    const statusClasses: { [key: string]: string } = {
      'pending': 'bg-yellow-100 text-yellow-800',
      'billed': 'bg-blue-100 text-blue-800',
      'paid': 'bg-green-100 text-green-800',
      'cancelled': 'bg-red-100 text-red-800'
    };
    return statusClasses[status] || 'bg-gray-100 text-gray-800';
  }

  getProjectTypeLabel(type: string): string {
    const typeLabels: { [key: string]: string } = {
      'CONSTRUCTION': 'Construction',
      'ROAD': 'Road',
      'UTILITIES': 'Utilities',
      'CONSULTANCY': 'Consultancy'
    };
    return typeLabels[type] || type;
  }

  updateBillStatus(bill: ProjectBill, newStatus: string) {
    const updateData: any = { status: newStatus };
    
    if (newStatus === 'billed' && !bill.billing_date) {
      updateData.billing_date = new Date().toISOString().split('T')[0];
    }
    
    if (newStatus === 'paid' && !bill.payment_date) {
      updateData.payment_date = new Date().toISOString().split('T')[0];
    }

    this.apiService.updateProjectBill(bill.id, updateData).subscribe({
      next: (res: any) => {
        if (res.success) {
          this.toastrService.success('Bill status updated successfully');
          this.loadProjectBills();
          this.loadSummary();
        }
      },
      error: (err: any) => {
        console.error('Error updating bill:', err);
        this.toastrService.error(err.error?.error || 'Failed to update bill');
      }
    });
  }

  clearFilters() {
    this.searchKeyword = '';
    this.selectedStatus = '';
    this.selectedProjectType = '';
    this.selectedProject = '';
    this.pageIndex = 0;
    this.loadProjectBills();
  }

  /**
   * Initiate payment for a bill using Razorpay
   */
  payBill(bill: ProjectBill) {
    if (bill.status === 'paid') {
      this.toastrService.warning('This bill is already paid');
      return;
    }

    this.loading = true;
    this.apiService.createBillPaymentOrder(bill.id).subscribe({
      next: (res: any) => {
        if (res.success && res.data) {
          const options = {
            key: res.data.key,
            amount: res.data.amount,
            currency: res.data.currency,
            name: 'Project Bill Payment',
            description: `Payment for ${bill.project_name}`,
            order_id: res.data.orderId,
            handler: (response: any) => {
              this.verifyPayment(bill.id, response);
            },
            prefill: {
              name: bill.org_name || 'Customer',
              email: '',
              contact: ''
            },
            theme: {
              color: '#3399cc'
            },
            modal: {
              ondismiss: () => {
                this.loading = false;
                this.toastrService.info('Payment cancelled');
              }
            }
          };

          const razorpayInstance = new (window as any).Razorpay(options);
          razorpayInstance.open();
          this.loading = false;
        } else {
          this.loading = false;
          this.toastrService.error('Failed to create payment order');
        }
      },
      error: (err: any) => {
        console.error('Error creating payment order:', err);
        this.loading = false;
        this.toastrService.error(err.error?.error || 'Failed to initiate payment');
      }
    });
  }

  /**
   * Verify payment after Razorpay callback
   */
  verifyPayment(billId: number, paymentResponse: any) {
    this.loading = true;
    this.apiService.verifyBillPayment(billId, {
      razorpay_order_id: paymentResponse.razorpay_order_id,
      razorpay_payment_id: paymentResponse.razorpay_payment_id,
      razorpay_signature: paymentResponse.razorpay_signature
    }).subscribe({
      next: (res: any) => {
        if (res.success) {
          this.toastrService.success('Payment successful! Bill has been marked as paid.');
          this.loadProjectBills();
          this.loadSummary();
        } else {
          this.toastrService.error('Payment verification failed');
        }
        this.loading = false;
      },
      error: (err: any) => {
        console.error('Error verifying payment:', err);
        this.toastrService.error(err.error?.error || 'Payment verification failed');
        this.loading = false;
      }
    });
  }

  /**
   * View bill details in a dialog
   */
  viewBill(bill: ProjectBill) {
    this.loading = true;
    this.apiService.getProjectBillById(bill.id).subscribe({
      next: (res: any) => {
        if (res.success) {
          const billData = res.data;
          // Open dialog with bill details
          const dialogRef = this.dialog.open(BillDetailsDialogComponent, {
            width: '800px',
            maxWidth: '90vw',
            data: billData
          });
        } else {
          this.toastrService.error('Failed to load bill details');
        }
        this.loading = false;
      },
      error: (err: any) => {
        console.error('Error loading bill details:', err);
        this.toastrService.error(err.error?.error || 'Failed to load bill details');
        this.loading = false;
      }
    });
  }

  /**
   * Edit bill amount (superadmin only)
   */
  editBillAmount(bill: ProjectBill) {
    const dialogRef = this.dialog.open(EditBillAmountDialogComponent, {
      width: '500px',
      data: {
        billId: bill.id,
        currentAmount: bill.bill_amount,
        projectName: bill.project_name
      }
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result && result.amount !== undefined && result.remark) {
        this.updateBillAmount(bill.id, result.amount, result.remark);
      }
    });
  }

  /**
   * Update bill amount via API
   */
  updateBillAmount(billId: number, newAmount: number, remark: string) {
    this.loading = true;
    this.apiService.updateProjectBill(billId, { 
      bill_amount: newAmount,
      update_remark: remark
    }).subscribe({
      next: (res: any) => {
        if (res.success) {
          this.toastrService.success('Bill amount updated successfully. Change has been tracked.');
          this.loadProjectBills();
          this.loadSummary();
        }
        this.loading = false;
      },
      error: (err: any) => {
        console.error('Error updating bill amount:', err);
        this.toastrService.error(err.error?.error || 'Failed to update bill amount');
        this.loading = false;
      }
    });
  }

  /**
   * Download invoice as PDF
   */
  downloadInvoice(bill: ProjectBill) {
    this.loading = true;
    this.apiService.getProjectBillById(bill.id).subscribe({
      next: (res: any) => {
        if (res.success) {
          const billData = res.data;
          const invoiceHTML = this.generateInvoiceHTML(billData);
          
          // Open in new window for printing/downloading
          const printWindow = window.open('', '_blank', 'width=800,height=600');
          if (printWindow) {
            printWindow.document.write(invoiceHTML);
            printWindow.document.close();
            
            printWindow.onload = () => {
              setTimeout(() => {
                printWindow.print();
              }, 250);
            };
          } else {
            this.toastrService.error('Please allow popups to download the invoice');
          }
        } else {
          this.toastrService.error('Failed to load bill details');
        }
        this.loading = false;
      },
      error: (err: any) => {
        console.error('Error loading bill details:', err);
        this.toastrService.error(err.error?.error || 'Failed to load bill details');
        this.loading = false;
      }
    });
  }

  /**
   * Generate invoice HTML for PDF
   */
  generateInvoiceHTML(bill: any): string {
    const invoiceDate = bill.billing_date 
      ? new Date(bill.billing_date).toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' })
      : new Date(bill.created_at).toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' });
    
    const paymentDate = bill.payment_date 
      ? new Date(bill.payment_date).toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' })
      : null;

    const invoiceNumber = bill.invoice_number || `BILL-${bill.id}`;
    const status = bill.status || 'pending';
    const statusColor = status === 'paid' ? '#10b981' : status === 'billed' ? '#3b82f6' : '#f59e0b';

    // Calculate GST (assuming 18% if bill amount is inclusive)
    const billAmount = parseFloat(bill.bill_amount) || 0;
    const gstRate = 0.18;
    const gstAmount = billAmount * gstRate / (1 + gstRate);
    const subtotal = billAmount - gstAmount;

    // Pre-calculate values for template string
    const projectTypeLabel = this.getProjectTypeLabel(bill.project_type || '');
    const formattedUnitRate = this.formatCurrency(parseFloat(bill.unit_rate) || 0);
    const formattedBillAmount = this.formatCurrency(billAmount);
    const formattedSubtotal = this.formatCurrency(subtotal);
    const formattedGstAmount = this.formatCurrency(gstAmount);
    const quantityDisplay = bill.rate_type === 'per_project' ? 'Project' : 'sqm';

    return `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Invoice ${invoiceNumber}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
      background: #fff;
      color: #1f2937;
      padding: 40px;
      line-height: 1.6;
    }
    .invoice-container {
      max-width: 800px;
      margin: 0 auto;
      background: #fff;
    }
    
    /* Header */
    .invoice-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      padding-bottom: 30px;
      border-bottom: 3px solid #3b82f6;
      margin-bottom: 30px;
    }
    .company-info h1 {
      font-size: 32px;
      font-weight: 700;
      color: #1e40af;
      margin-bottom: 5px;
    }
    .company-info p {
      color: #6b7280;
      font-size: 14px;
      margin: 2px 0;
    }
    .invoice-title {
      text-align: right;
    }
    .invoice-title h2 {
      font-size: 36px;
      font-weight: 700;
      color: #1e40af;
      letter-spacing: 2px;
    }
    .invoice-number {
      font-size: 16px;
      color: #374151;
      font-weight: 600;
      margin-top: 5px;
    }
    .invoice-date {
      font-size: 14px;
      color: #6b7280;
      margin-top: 3px;
    }
    .status-badge {
      display: inline-block;
      padding: 6px 16px;
      border-radius: 20px;
      font-size: 12px;
      font-weight: 600;
      margin-top: 10px;
      background: ${statusColor};
      color: white;
    }
    
    /* Bill To Section */
    .bill-to-section {
      display: flex;
      justify-content: space-between;
      margin-bottom: 30px;
    }
    .bill-to, .bill-from {
      flex: 1;
    }
    .bill-to h3, .bill-from h3 {
      font-size: 14px;
      color: #6b7280;
      text-transform: uppercase;
      letter-spacing: 1px;
      margin-bottom: 10px;
    }
    .bill-to p, .bill-from p {
      color: #1f2937;
      font-size: 14px;
      margin: 3px 0;
    }
    
    /* Items Table */
    .items-table {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 30px;
    }
    .items-table thead {
      background: #f3f4f6;
    }
    .items-table th {
      padding: 12px;
      text-align: left;
      font-size: 12px;
      font-weight: 600;
      color: #374151;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
    .items-table td {
      padding: 12px;
      border-bottom: 1px solid #e5e7eb;
      font-size: 14px;
      color: #1f2937;
    }
    .items-table tbody tr:last-child td {
      border-bottom: 2px solid #3b82f6;
    }
    .text-right {
      text-align: right;
    }
    .text-center {
      text-align: center;
    }
    
    /* Totals Section */
    .totals-section {
      display: flex;
      justify-content: flex-end;
      margin-bottom: 30px;
    }
    .totals-table {
      width: 300px;
    }
    .totals-table tr {
      display: flex;
      justify-content: space-between;
      padding: 8px 0;
    }
    .totals-table .label {
      color: #6b7280;
      font-size: 14px;
    }
    .totals-table .value {
      color: #1f2937;
      font-size: 14px;
      font-weight: 500;
    }
    .totals-table .total-row {
      border-top: 2px solid #3b82f6;
      padding-top: 12px;
      margin-top: 8px;
    }
    .totals-table .total-row .label,
    .totals-table .total-row .value {
      font-size: 18px;
      font-weight: 700;
      color: #1e40af;
    }
    
    /* Footer */
    .invoice-footer {
      margin-top: 40px;
      padding-top: 20px;
      border-top: 1px solid #e5e7eb;
      text-align: center;
      color: #6b7280;
      font-size: 12px;
    }
    
    @media print {
      body { padding: 20px; }
      .no-print { display: none; }
    }
  </style>
</head>
<body>
  <div class="invoice-container">
    <!-- Header -->
    <div class="invoice-header">
      <div class="company-info">
        <h1>${bill.org_name || 'Company Name'}</h1>
        <p>${bill.org_address || 'Address'}</p>
        ${bill.org_gst ? `<p>GSTIN: ${bill.org_gst}</p>` : ''}
        ${bill.org_pan ? `<p>PAN: ${bill.org_pan}</p>` : ''}
        ${bill.org_phone ? `<p>Phone: ${bill.org_phone}</p>` : ''}
        ${bill.org_email ? `<p>Email: ${bill.org_email}</p>` : ''}
      </div>
      <div class="invoice-title">
        <h2>INVOICE</h2>
        <div class="invoice-number">Invoice #: ${invoiceNumber}</div>
        <div class="invoice-date">Date: ${invoiceDate}</div>
        ${paymentDate ? `<div class="invoice-date">Payment Date: ${paymentDate}</div>` : ''}
        <div class="status-badge">${status.toUpperCase()}</div>
      </div>
    </div>

    <!-- Bill To Section -->
    <div class="bill-to-section">
      <div class="bill-from">
        <h3>From</h3>
        <p><strong>${bill.org_name || 'Company Name'}</strong></p>
        <p>${bill.org_address || 'Address'}</p>
        ${bill.org_gst ? `<p>GSTIN: ${bill.org_gst}</p>` : ''}
      </div>
      <div class="bill-to">
        <h3>Bill To</h3>
        <p><strong>${bill.bill_to_name || bill.client_name || 'Client'}</strong></p>
        <p>${bill.project_name}</p>
        <p>Project Type: ${projectTypeLabel}</p>
        ${bill.domain ? `<p>Domain: ${bill.domain}${bill.sub_domain ? ' - ' + bill.sub_domain : ''}</p>` : ''}
      </div>
    </div>

    <!-- Items Table -->
    <table class="items-table">
      <thead>
        <tr>
          <th>Description</th>
          <th class="text-center">Quantity</th>
          <th class="text-right">Unit Rate</th>
          <th class="text-right">Amount</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>
            <strong>${bill.project_name}</strong><br>
            <small style="color: #6b7280;">
              Project Type: ${projectTypeLabel}<br>
              ${bill.domain ? `Domain: ${bill.domain}${bill.sub_domain ? ' - ' + bill.sub_domain : ''}` : ''}<br>
              ${bill.rate_type === 'per_sq_mt' ? 'Rate: Per Square Meter' : bill.rate_type === 'per_psm' ? 'Rate: Per Square Meter (PSM)' : 'Rate: Per Project'}
            </small>
          </td>
          <td class="text-center">${parseFloat(bill.quantity || 0).toFixed(2)} ${quantityDisplay}</td>
          <td class="text-right">${formattedUnitRate}</td>
          <td class="text-right">${formattedBillAmount}</td>
        </tr>
      </tbody>
    </table>

    <!-- Totals Section -->
    <div class="totals-section">
      <table class="totals-table">
        <tr>
          <td class="label">Subtotal:</td>
          <td class="value">${formattedSubtotal}</td>
        </tr>
        <tr>
          <td class="label">GST (18%):</td>
          <td class="value">${formattedGstAmount}</td>
        </tr>
        <tr class="total-row">
          <td class="label">Total:</td>
          <td class="value">${formattedBillAmount}</td>
        </tr>
      </table>
    </div>

    <!-- Payment Info -->
    ${bill.payment_date ? `
    <div style="background: #f0fdf4; padding: 15px; border-radius: 8px; margin-bottom: 20px;">
      <p style="color: #10b981; font-weight: 600; margin-bottom: 5px;">✓ Payment Received</p>
      <p style="color: #374151; font-size: 14px;">Payment Date: ${paymentDate}</p>
      ${bill.metadata?.razorpay_payment_id ? `<p style="color: #374151; font-size: 12px;">Transaction ID: ${bill.metadata.razorpay_payment_id}</p>` : ''}
    </div>
    ` : ''}

    <!-- Footer -->
    <div class="invoice-footer">
      <p>Thank you for your business!</p>
      <p style="margin-top: 10px;">This is a computer-generated invoice and does not require a signature.</p>
    </div>
  </div>
</body>
</html>
    `;
  }
}

